import { brand } from '../../../theme/themePrimitives';
import CustomDropdown from '../../reusable/dropdown';
import CustomModal from '../../modals';
import LabelWrapper from '../../wrapper/label';
import PipelineApi from '../../../apis/pipeline';
import { replaceToken } from '../../../redux/slices';
import { showError } from '../../../redux/slices/error';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, SelectChangeEvent, Typography } from '@mui/material';
import { fetchDealDetailThunk, transferDealThunk } from '../../../redux/thunks/deal';
import { PipelineObject, StageObject, TransferDealParams } from '../../../params/pipeline';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

/**
 * @heading1: TransferDealModal
 * @paragraph:A modal component for transferring a deal to another stage or pipeline.
 * Users can select a pipeline and stage to transfer the deal.
 * @param: {boolean} open - Determines whether the modal is open.
 * @param: {function} onClose - Function to handle closing the modal.
 */

const TransferDealModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { pipelines, selectedPipeline: selectedPipe } = useSelector((state: RootState) => state.pipeline);
    const [selectedPipeline, setSelectedPipeline] = useState<PipelineObject | null>(selectedPipe as PipelineObject);
    const [selectedStage, setSelectedStage] = useState<StageObject | null>(null);
    const { token } = useSelector((state: RootState) => state.app);
    const { selectedDeal, loaders } = useSelector((state: RootState) => state.deal);

    // Fetch pipeline details when the modal opens and selectedPipeline exists
    useEffect(() => {
        if (selectedPipe?.id) {
            (async () => {
                try {
                    const response = await PipelineApi.getSinglePipelineDetail(token, selectedPipe.id);
                    if (response.success) {
                        dispatch(replaceToken(response.token));
                        setSelectedPipeline(response.pipeline as PipelineObject);
                        const selectedStageFromDeal = (response.pipeline as PipelineObject)?.pipeline_stages?.find((stage) => stage.id === selectedDeal?.stage_id);
                        setSelectedStage(selectedStageFromDeal as StageObject);
                    } else {
                        dispatch(showError({
                            title: 'Error',
                            description: response.msg
                        }));
                    }
                } catch (e) {
                    //
                }
            })();
        }
    }, [selectedPipe, token, dispatch]);

    useEffect(() => {
        if (selectedDeal) {
            const selectedStageFromDeal = selectedPipeline?.pipeline_stages?.find((stage) => stage.id === selectedDeal?.stage_id);
            setSelectedStage(selectedStageFromDeal as StageObject);
        }
    }, [selectedDeal, selectedPipeline]);

    // Handles pipeline selection
    const handlePipelineChange = async (event: SelectChangeEvent<unknown>) => {
        event.preventDefault();
        const pipelineId = parseInt(event.target.value as string, 10);
        try {
            const response = await PipelineApi.getSinglePipelineDetail(token, pipelineId);
            if (response.success) {
                dispatch(replaceToken(response.token));
                setSelectedPipeline(response.pipeline as PipelineObject);
                setSelectedStage(null);
            } else {
                dispatch(showError({
                    title: 'Error',
                    description: response.msg
                }));
            }
        } catch (e) {
            console.error(e);
        }
    };

    // Handles stage selection
    const handleStageChange = (event: SelectChangeEvent<unknown>) => {
        const stageId = Number(event.target.value);
        setSelectedStage(selectedPipeline?.pipeline_stages?.find((stage) => stage.id === stageId) || null);
    };

    // Submit transfer deal request
    const onSubmitHandler = async () => {
        if (!selectedPipeline || !selectedStage) return;
        try {
            const data: TransferDealParams = {
                deal_id: selectedDeal?.id as number,
                target_pipeline_id: selectedPipeline.id,
                target_stage_id: selectedStage.id
            };
            await dispatch(transferDealThunk(data)).unwrap();
            onClose();
            await dispatch(fetchDealDetailThunk(selectedDeal?.id || 0)).unwrap();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <CustomModal open={open} handleClose={loaders.isUpdating ? () => null : onClose} title="Transfer Deals">
            <Typography variant="body1" align="center" mb={2}>
                Transfer <Typography component="span" variant="body1" color={brand[300]} >{selectedDeal?.title} {' '}</Typography>
                deal to another stage or pipeline.
            </Typography>

            {/* Pipeline and Stage Selection */}
            <Box display="flex" gap="20px" width="100%" boxSizing="border-box">
                <Box width="100%" boxSizing="border-box">
                    <LabelWrapper label="Pipeline" />
                    {pipelines.length !== 0 && (
                        <CustomDropdown
                            options={pipelines.map((pipe) => ({
                                label: pipe.name,
                                value: pipe.id
                            }))}
                            onChange={handlePipelineChange}
                            value={selectedPipeline?.id || ''}
                            disabled={loaders.isUpdating}
                            sx={{ maxWidth: 210 }}
                        />
                    )}
                </Box>
                <Box width="100%" boxSizing="border-box">
                    <LabelWrapper label="Stage" />
                    <CustomDropdown
                        options={
                            selectedPipeline?.pipeline_stages
                                ? selectedPipeline.pipeline_stages.map((stage) => ({
                                    label: stage.stage_name,
                                    value: stage.id
                                }))
                                : []
                        }
                        onChange={handleStageChange}
                        value={selectedStage?.id || ''}
                        disabled={loaders.isUpdating || !selectedPipeline}
                        sx={{ maxWidth: 210 }}
                    />
                </Box>
            </Box>

            {/* Action Buttons */}
            <Box display="flex" gap={2} mt={2} boxSizing="border-box">
                <Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={loaders.isUpdating}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={onSubmitHandler}
                    fullWidth
                    disabled={loaders.isUpdating || !selectedPipeline || !selectedStage || selectedStage?.id === selectedDeal?.stage_id}
                >
                    {loaders.isUpdating ? 'Transferring...' : 'Transfer'}
                </Button>
            </Box>
        </CustomModal>
    );
};

export default TransferDealModal;
